import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { Loader2, AlertCircle } from 'lucide-react'
import { ApiHttpError, verifyMagicLink } from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect'
import { defaultLocale, isLocale } from '@/lib/locales'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'

/**
 * Atterrissage depuis l'email magique : extrait ?token=... de l'URL, l'échange
 * contre des JWT via /api/auth/magic-link/verify/, puis bascule vers le
 * dashboard via la même mécanique que le login email/password.
 */
export function MagicCallbackPage() {
  const { t } = useTranslation()
  const { locale } = useParams<{ locale: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const setTokens = useAuthStore((s) => s.setTokens)
  const clearAuth = useAuthStore((s) => s.clear)
  const postLoginRedirect = usePostLoginRedirect()
  const startedRef = useRef(false)

  const effectiveLocale = isLocale(locale) ? locale : defaultLocale

  const [error, setError] = useState<string | null>(null)
  const [emailNotVerified, setEmailNotVerified] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (token: string) => {
      const tokens = await verifyMagicLink(token)
      clearAuth()
      setTokens(tokens.access, tokens.refresh)
      await postLoginRedirect(effectiveLocale)
      window.dispatchEvent(new Event('login-complete'))
    },
    onError: (err) => {
      console.error('[MagicCallback]', err)
      if (err instanceof ApiHttpError) {
        if (err.status === 403) {
          const data = err.data as { error?: string; email?: string }
          if (data?.error === 'email_not_verified' && data.email) {
            setEmailNotVerified(data.email)
            return
          }
        }
      }
      setError(
        t(
          'magicCallback.invalidLink',
          'Ce lien est expiré ou invalide. Demandez-en un nouveau depuis la page de connexion.',
        ),
      )
    },
  })

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const token = searchParams.get('token')
    if (!token) {
      setError(t('magicCallback.missingToken', 'Lien invalide.'))
      return
    }
    mutation.mutate(token)
  }, [searchParams, mutation, t])

  // Email non vérifié : on redirige vers la page verify-email (cohérence avec
  // le reste du flow auth)
  useEffect(() => {
    if (emailNotVerified) {
      navigate(
        `/${effectiveLocale}/verify-email?email=${encodeURIComponent(emailNotVerified)}&from=magic`,
        { replace: true },
      )
    }
  }, [emailNotVerified, effectiveLocale, navigate])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A192F] text-white px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
          <h2 className="text-xl font-semibold">
            {t('magicCallback.errorTitle', 'Impossible de vous connecter')}
          </h2>
          <p className="text-white/80 mt-2">{error}</p>
          <Button
            type="button"
            onClick={() => navigate(`/${effectiveLocale}/login`)}
            className="mt-6 bg-white text-[#0A192F] hover:bg-white/90"
          >
            {t('magicCallback.backToLogin', 'Retour à la connexion')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A192F] text-white">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold">
          {t('magicCallback.connecting', 'Connexion en cours…')}
        </h2>
        <p className="text-white/80 mt-2">
          {t('magicCallback.subtitle', 'Vérification de votre lien magique.')}
        </p>
      </div>
    </div>
  )
}
